import { defaultCampaignBudget } from '@/constants'
import { CampaignModel } from '@/ui'
import { type CustomFieldWithValueData, type QuestionWithScoreData, useCampaignsShow } from '@jobilla/nexus-sdk'
import { parseISO } from 'date-fns'

export type UseCampaignDataArgs = {
	campaignId: string
}

export type UseCampaignDataReturns = {
	campaign: CampaignModel
	formQuestions: QuestionWithScoreData[]
	isLoading: boolean
}

export function useCampaignData({ campaignId }: UseCampaignDataArgs): UseCampaignDataReturns {
	const campaign = useCampaignsShow(campaignId)

	return {
		campaign: {
			id: campaign?.data?.id ?? '',
			title: campaign?.data?.title ?? '',
			status: (campaign?.data?.status as 'review' | 'draft' | 'active') ?? 'review',
			location: campaign?.data?.location ?? 'Unknown location',
			language: campaign?.data?.language ?? 'English',
			budget: campaign?.data?.budget ?? defaultCampaignBudget,
			addons: campaign?.data?.addons ?? [],
			customFields: (campaign?.data?.customFields ?? []) as CustomFieldWithValueData[],
			serviceFeePercent: campaign?.data?.serviceFeePercent ?? 0,
			budgetBreakdown: campaign?.data?.budgetBreakdown
				? {
					metaBudget: campaign.data.budgetBreakdown.metaBudget,
					serviceFee: campaign.data.budgetBreakdown.serviceFee,
				}
				: null,
			requirements: campaign?.data?.requirements ?? '',
			invoiceUrl: campaign?.data?.invoiceUrl ?? '',
			formId: campaign?.data?.form?.id ?? '',
			image: campaign?.data?.image ?? '',
			storyImage: campaign?.data?.storyImage ?? '',
			startedAt: campaign.data?.startDate ? parseISO(campaign.data.startDate) : null,
			endsAt: campaign.data?.endDate ? parseISO(campaign.data.endDate) : null,
			isEvergreen: campaign?.data?.isEvergreen ?? false,
			isArchived: campaign?.data?.isArchived ?? false,
			createdAt: new Date(campaign?.data?.createdAt ?? ''),
			updatedAt: new Date(campaign?.data?.updatedAt ?? ''),
			adCampaignLocations: campaign?.data?.adCampaignLocations ?? [],
			submittedForReviewAt: campaign?.data?.submittedForReviewAt
				? new Date(campaign.data.submittedForReviewAt)
				: null,
			submittedForReviewBy: campaign?.data?.submittedForReviewBy ?? null,
			reviewedAt: campaign?.data?.reviewedAt
				? new Date(campaign.data.reviewedAt)
				: null,
			reviewedBy: campaign?.data?.reviewedBy ?? null,
			firstContactAutomationEnabled: campaign?.data?.firstContactAutomationEnabled ?? null,
			firstContactScoreThreshold: campaign?.data?.firstContactScoreThreshold ?? null,
			organisationId: campaign?.data?.organisationId ?? null,
			metaAdCampaign: campaign?.data?.metaAdCampaign
				? {
					startDate: campaign.data.metaAdCampaign.startDate
						? new Date(campaign.data.metaAdCampaign.startDate)
						: null,
					endDate: campaign.data.metaAdCampaign.endDate
						? new Date(campaign.data.metaAdCampaign.endDate)
						: null,
				}
				: null,
		},
		formQuestions: campaign?.data?.form?.questions ?? [],
		isLoading: campaign.isLoading,
	}
}
